import React, { createContext, useContext, useCallback, useRef, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { logger } from '../utils/logger';
import { debounce } from 'lodash';

/**
 * Topic-specific filter state
 */
interface TopicFilterState {
  searchQuery: string;
  selectedUserIds: string[];
  selectedTags: string[];
  showUnscheduledOnly: boolean;
  showUnassignedOnly: boolean;
  showHighlightedOnly: boolean;
  sortByDeadline: boolean;
}

/**
 * Default filter values
 */
const DEFAULT_FILTERS: TopicFilterState = {
  searchQuery: '',
  selectedUserIds: [],
  selectedTags: [],
  showUnscheduledOnly: false,
  showUnassignedOnly: false,
  showHighlightedOnly: false,
  sortByDeadline: false,
};

/**
 * Filter actions interface
 */
interface TopicFilterActions {
  setSearchQuery: (query: string) => void;
  setSelectedUserIds: (userIds: string[]) => void;
  setSelectedTags: (tags: string[]) => void;
  toggleUnscheduledOnly: () => void;
  toggleUnassignedOnly: () => void;
  toggleHighlightedOnly: () => void;
  toggleSortByDeadline: () => void;
  resetFilters: () => void;
}

/**
 * Complete context value interface
 */
interface TopicFiltersContextValue extends TopicFilterState, TopicFilterActions {
  topicId: string;
}

/**
 * BroadcastChannel for cross-tab synchronization
 * Wrapped in a function to handle environments where BroadcastChannel is not available
 */
const createBroadcastChannel = () => {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      return new BroadcastChannel('zentime-topic-filters');
    } catch (error) {
      logger.warn('BroadcastChannel not available:', error);
      return null;
    }
  }
  return null;
};

/**
 * Message types for cross-tab communication
 */
interface FilterSyncMessage {
  type: 'FILTER_UPDATE';
  topicId: string;
  filters: TopicFilterState;
  timestamp: number;
  token: string; // Added for security verification
}

/**
 * Generate a security token for the current session
 * This ensures messages are from the same application instance
 */
const SECURITY_TOKEN = `zentime-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

/**
 * Generate a stable hash for filter state comparison
 * This is more efficient than JSON.stringify for frequent comparisons
 */
function getFilterStateHash(state: TopicFilterState): string {
  // Create a stable string representation
  const parts = [
    state.searchQuery,
    state.selectedUserIds.join(','),
    state.selectedTags.join(','),
    state.unscheduledOnly ? '1' : '0',
    state.unassignedOnly ? '1' : '0',
    state.highlightedOnly ? '1' : '0',
    state.sortByDeadline ? '1' : '0',
  ];
  return parts.join('|');
}

/**
 * LRU Cache for filter stores with memory management
 */
class FilterStoreCache {
  private cache = new Map<string, ReturnType<typeof createFilterStore>>();
  private accessOrder: string[] = [];
  private readonly maxSize = 10; // Maximum number of topics to keep in memory
  private broadcastChannel = createBroadcastChannel();
  private debouncedPersistMap = new Map<string, ReturnType<typeof debounce>>();
  private readonly securityToken = SECURITY_TOKEN;

  get(topicId: string): ReturnType<typeof createFilterStore> {
    let store = this.cache.get(topicId);

    if (!store) {
      store = createFilterStore(topicId);
      this.cache.set(topicId, store);
      this.accessOrder.push(topicId);

      // Evict least recently used if cache is full
      if (this.accessOrder.length > this.maxSize) {
        const lru = this.accessOrder.shift()!;
        const evictedStore = this.cache.get(lru);
        if (evictedStore) {
          // Save to localStorage before evicting
          this.persistFilters(lru, evictedStore.getState());
          evictedStore.destroy();
          this.cache.delete(lru);
        }
      }
    } else {
      // Move to end (most recently used)
      const index = this.accessOrder.indexOf(topicId);
      if (index > -1) {
        this.accessOrder.splice(index, 1);
        this.accessOrder.push(topicId);
      }
    }

    return store;
  }

  cleanupAll(): void {
    // Flush all pending debounced operations
    for (const [, debouncedPersist] of this.debouncedPersistMap.entries()) {
      debouncedPersist.flush();
    }

    for (const [topicId, store] of this.cache.entries()) {
      this.persistFilters(topicId, store.getState());
      store.destroy();
    }
    this.cache.clear();
    this.accessOrder = [];
    this.debouncedPersistMap.clear();

    // Close broadcast channel
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }

  /**
   * Cleanup a specific topic's resources
   * Called when a topic is unmounted or changes
   */
  cleanup(topicId: string): void {
    // Cancel any pending persistence for this topic
    const debouncedPersist = this.debouncedPersistMap.get(topicId);
    if (debouncedPersist) {
      debouncedPersist.cancel();
      this.debouncedPersistMap.delete(topicId);
    }

    // Note: We don't remove from cache here as another component might still be using it
    // The LRU will handle cache eviction when needed
  }

  setupBroadcastListener(): void {
    if (!this.broadcastChannel) return;

    this.broadcastChannel.onmessage = (event: MessageEvent<unknown>) => {
      try {
        // Validate message structure using type guard
        if (!this.isFilterSyncMessage(event.data)) {
          logger.warn('Invalid broadcast message format');
          return;
        }

        const data = event.data; // Now properly typed as FilterSyncMessage

        // Validate security token
        if (data.token !== this.securityToken) {
          logger.warn('Invalid broadcast message: security token mismatch');
          return;
        }

        // Validate timestamp is recent (within last 5 seconds) to prevent replay attacks
        const age = Date.now() - data.timestamp;
        if (age > 5000 || age < 0) {
          logger.warn('Invalid broadcast message: stale or future timestamp', { age });
          return;
        }

        const { topicId, filters } = data;

        // Update the store if it exists in cache
        const store = this.cache.get(topicId);
        if (store) {
          // Check if filters have actually changed using efficient hash comparison
          const currentState = store.getState();
          const hasChanges = getFilterStateHash(currentState) !== getFilterStateHash(filters);

          if (hasChanges) {
            store.setState(filters);
            logger.info(`Synced filters from another tab for topic: ${topicId}`);
          }
        }
      } catch (error) {
        logger.error('Error processing broadcast message:', error);
      }
    };
  }

  /**
   * Validate filter state structure
   */
  private isValidFilterState(filters: any): filters is TopicFilterState {
    if (!filters || typeof filters !== 'object') return false;

    // Check required string fields
    if (typeof filters.searchQuery !== 'string') return false;

    // Check required array fields
    if (!Array.isArray(filters.selectedUserIds)) return false;
    if (!Array.isArray(filters.selectedTags)) return false;

    // Check all array elements are strings
    if (!filters.selectedUserIds.every((id: any) => typeof id === 'string')) return false;
    if (!filters.selectedTags.every((tag: any) => typeof tag === 'string')) return false;

    // Check required boolean fields
    if (typeof filters.unscheduledOnly !== 'boolean') return false;
    if (typeof filters.unassignedOnly !== 'boolean') return false;
    if (typeof filters.highlightedOnly !== 'boolean') return false;
    if (typeof filters.sortByDeadline !== 'boolean') return false;

    return true;
  }

  /**
   * Type guard for FilterSyncMessage
   */
  private isFilterSyncMessage(data: unknown): data is FilterSyncMessage {
    if (!data || typeof data !== 'object') return false;

    const msg = data as Record<string, unknown>;

    // Check message type
    if (msg.type !== 'FILTER_UPDATE') return false;

    // Check required fields
    if (typeof msg.topicId !== 'string') return false;
    if (typeof msg.timestamp !== 'number') return false;
    if (typeof msg.token !== 'string') return false;
    if (!msg.filters || typeof msg.filters !== 'object') return false;

    // Validate filters structure
    return this.isValidFilterState(msg.filters);
  }

  persistFilters(topicId: string, filters: TopicFilterState): void {
    // Get or create debounced persist function for this topic
    let debouncedPersist = this.debouncedPersistMap.get(topicId);
    if (!debouncedPersist) {
      debouncedPersist = debounce((topicId: string, filters: TopicFilterState) => {
        try {
          const key = `topic-filters-${topicId}`;
          localStorage.setItem(key, JSON.stringify(filters));

          // Broadcast to other tabs
          if (this.broadcastChannel) {
            const message: FilterSyncMessage = {
              type: 'FILTER_UPDATE',
              topicId,
              filters,
              timestamp: Date.now(),
              token: this.securityToken,
            };
            this.broadcastChannel.postMessage(message);
          }
        } catch (error) {
          logger.error('Failed to persist topic filters:', error);
        }
      }, 500); // 500ms debounce

      this.debouncedPersistMap.set(topicId, debouncedPersist);
    }

    // Call the debounced function
    debouncedPersist(topicId, filters);
  }

  loadPersistedFilters(topicId: string): Partial<TopicFilterState> | null {
    try {
      const key = `topic-filters-${topicId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      logger.error('Failed to load persisted topic filters:', error);
    }
    return null;
  }
}

// Global cache instance
const filterStoreCache = new FilterStoreCache();
// Setup broadcast listener for cross-tab sync
filterStoreCache.setupBroadcastListener();

/**
 * Create a filter store for a specific topic
 */
function createFilterStore(topicId: string) {
  // Load persisted filters
  const persisted = filterStoreCache.loadPersistedFilters(topicId);
  const initialState = { ...DEFAULT_FILTERS, ...persisted };

  return create<TopicFilterState>()(
    subscribeWithSelector(set => ({
      ...initialState,

      // Update methods that trigger persistence
      setSearchQuery: (query: string) => set({ searchQuery: query }),
      setSelectedUserIds: (userIds: string[]) => set({ selectedUserIds: userIds }),
      setSelectedTags: (tags: string[]) => set({ selectedTags: tags }),
      toggleUnscheduledOnly: () =>
        set(state => ({ showUnscheduledOnly: !state.showUnscheduledOnly })),
      toggleUnassignedOnly: () => set(state => ({ showUnassignedOnly: !state.showUnassignedOnly })),
      toggleHighlightedOnly: () =>
        set(state => ({ showHighlightedOnly: !state.showHighlightedOnly })),
      toggleSortByDeadline: () => set(state => ({ sortByDeadline: !state.sortByDeadline })),
      resetFilters: () => set(DEFAULT_FILTERS),
    }))
  );
}

/**
 * Context for topic filters
 */
const TopicFiltersContext = createContext<TopicFiltersContextValue | null>(null);

/**
 * Provider component that manages filter state for a specific topic
 */
export function TopicFiltersProvider({
  topicId,
  children,
}: {
  topicId: string;
  children: React.ReactNode;
}) {
  const storeRef = useRef<ReturnType<typeof createFilterStore>>();

  // Get or create store for this topic
  if (!storeRef.current) {
    storeRef.current = filterStoreCache.get(topicId);
  }

  // Subscribe to store changes
  const [filters, setFilters] = React.useState<TopicFilterState>(() =>
    storeRef.current!.getState()
  );

  useEffect(() => {
    const store = storeRef.current!;
    const unsubscribe = store.subscribe(setFilters);

    return () => {
      unsubscribe();
    };
  }, [topicId]);

  // Create stable action methods
  const setSearchQuery = useCallback((query: string) => {
    storeRef.current?.setState({ searchQuery: query });
  }, []);

  const setSelectedUserIds = useCallback((userIds: string[]) => {
    storeRef.current?.setState({ selectedUserIds: userIds });
  }, []);

  const setSelectedTags = useCallback((tags: string[]) => {
    storeRef.current?.setState({ selectedTags: tags });
  }, []);

  const toggleUnscheduledOnly = useCallback(() => {
    storeRef.current?.setState(state => ({ showUnscheduledOnly: !state.showUnscheduledOnly }));
  }, []);

  const toggleUnassignedOnly = useCallback(() => {
    storeRef.current?.setState(state => ({ showUnassignedOnly: !state.showUnassignedOnly }));
  }, []);

  const toggleHighlightedOnly = useCallback(() => {
    storeRef.current?.setState(state => ({ showHighlightedOnly: !state.showHighlightedOnly }));
  }, []);

  const toggleSortByDeadline = useCallback(() => {
    storeRef.current?.setState(state => ({ sortByDeadline: !state.sortByDeadline }));
  }, []);

  const resetFilters = useCallback(() => {
    storeRef.current?.setState(DEFAULT_FILTERS);
  }, []);

  // Auto-persist on changes
  useEffect(() => {
    const store = storeRef.current!;
    const unsubscribe = store.subscribe(state => {
      filterStoreCache.persistFilters(topicId, state);
    });

    return () => {
      unsubscribe();
      // Cleanup debounced operations when topic changes
      filterStoreCache.cleanup(topicId);
    };
  }, [topicId]);

  const contextValue = useMemo<TopicFiltersContextValue>(
    () => ({
      topicId,
      ...filters,
      setSearchQuery,
      setSelectedUserIds,
      setSelectedTags,
      toggleUnscheduledOnly,
      toggleUnassignedOnly,
      toggleHighlightedOnly,
      toggleSortByDeadline,
      resetFilters,
    }),
    [
      topicId,
      filters,
      setSearchQuery,
      setSelectedUserIds,
      setSelectedTags,
      toggleUnscheduledOnly,
      toggleUnassignedOnly,
      toggleHighlightedOnly,
      toggleSortByDeadline,
      resetFilters,
    ]
  );

  return (
    <TopicFiltersContext.Provider value={contextValue}>{children}</TopicFiltersContext.Provider>
  );
}

/**
 * Hook to use topic filters
 * Throws if used outside of TopicFiltersProvider
 */
export function useTopicFilters() {
  const context = useContext(TopicFiltersContext);
  if (!context) {
    throw new Error('useTopicFilters must be used within a TopicFiltersProvider');
  }
  return context;
}

/**
 * Hook to cleanup filters when component unmounts
 * Use this in topic components to prevent memory leaks
 */
export function useTopicFiltersCleanup(topicId: string) {
  useEffect(() => {
    return () => {
      // Only cleanup if no other components are using this topic
      // This is handled by the LRU cache automatically
      logger.component(`Topic filters cleanup consideration for ${topicId}`);
    };
  }, [topicId]);
}

/**
 * Global cleanup function for app-level cleanup
 */
export function cleanupAllTopicFilters() {
  filterStoreCache.cleanupAll();
}
